/**
 * Event log — the source of truth (docs/04-DATA-ARCHITECTURE.md §0).
 *
 * State is derived, never authored. Every change goes append(event) -> reduce.
 * That single constraint buys replay, save integrity, honest Chronicle text,
 * and NPC memories that cannot drift out of sync with what actually happened.
 */

import type { EntityId, JsonValue, RoomId } from './types.js';

/**
 * Significance gates persistence (docs/04 §1.1). This is the primary defence
 * against World State Explosion, the risk the CTO review flagged at 7.5/10:
 * we do not record that the player walked three squares, only that they spared
 * the wizard.
 */
export type Significance = 'trivial' | 'notable' | 'pivotal' | 'legendary';

export const SIGNIFICANCE_RANK: Readonly<Record<Significance, number>> = {
  trivial: 0,
  notable: 1,
  pivotal: 2,
  legendary: 3,
};

export function atLeast(a: Significance, b: Significance): boolean {
  return SIGNIFICANCE_RANK[a] >= SIGNIFICANCE_RANK[b];
}

export type EventType =
  // run lifecycle
  | 'RUN_STARTED'
  | 'RUN_ENDED'
  | 'CLASS_CHOSEN'
  // dice
  | 'DICE_ROLLED'
  | 'DICE_MANIPULATED'
  | 'FATE_DEBT_THRESHOLD'
  | 'FATE_CHAIN_TRIGGERED'
  | 'BAD_LUCK_TRIGGERED'
  // movement / rooms
  | 'MOVED'
  | 'ROOM_ENTERED'
  | 'ROOM_CLEARED'
  | 'SECRET_DISCOVERED'
  | 'RESTED'
  | 'MOVE_FORFEITED'
  // encounters
  | 'ENCOUNTER_STARTED'
  | 'CHOICE_MADE'
  | 'CHOICE_TIMED_OUT'
  // combat
  | 'COMBAT_STARTED'
  | 'COMBAT_WON'
  | 'COMBAT_FLED'
  | 'MONSTER_KILLED'
  | 'MONSTER_SPARED'
  | 'BOSS_KILLED'
  | 'BOSS_SPARED'
  | 'BOSS_RECRUITED'
  | 'PLAYER_DIED'
  // npc
  | 'NPC_MET'
  | 'NPC_SPOKE'
  | 'NPC_HELPED'
  | 'NPC_SAVED'
  | 'NPC_KILLED'
  | 'NPC_BETRAYED'
  | 'NPC_ROBBED'
  | 'NPC_PERSUADED'
  | 'NPC_LIED_TO'
  | 'PROMISE_MADE'
  | 'PROMISE_KEPT'
  | 'PROMISE_BROKEN'
  // items / cards
  | 'ITEM_GAINED'
  | 'ITEM_LOST'
  | 'ITEM_USED'
  | 'CARD_DRAWN'
  | 'CARD_PLAYED'
  | 'CARD_TRANSFORMED'
  | 'CARD_REMOVED'
  // knowledge
  | 'FACT_LEARNED'
  | 'RUMOR_HEARD'
  // meta
  | 'EASTER_EGG_FOUND'
  | 'TITLE_EARNED'
  | 'CHRONICLE_CHAPTER'
  | 'ANALYTICS';

/** Default significance per event type. Callers may raise it, never silently lower it. */
export const DEFAULT_SIGNIFICANCE: Readonly<Record<EventType, Significance>> = {
  RUN_STARTED: 'notable',
  RUN_ENDED: 'pivotal',
  CLASS_CHOSEN: 'notable',

  DICE_ROLLED: 'trivial',
  DICE_MANIPULATED: 'trivial',
  FATE_DEBT_THRESHOLD: 'pivotal',
  FATE_CHAIN_TRIGGERED: 'notable',
  BAD_LUCK_TRIGGERED: 'notable',

  MOVED: 'trivial',
  ROOM_ENTERED: 'trivial',
  ROOM_CLEARED: 'notable',
  SECRET_DISCOVERED: 'pivotal',
  // 휴식은 두 번뿐인 선택이다 — 연대기에 남는다(GDD §12-1).
  RESTED: 'notable',
  // 갈 곳 없는 눈을 흘려보낸 것 — 굴림 기억(연속·불운)에는 남지만 연대기감은 아니다.
  MOVE_FORFEITED: 'trivial',

  ENCOUNTER_STARTED: 'trivial',
  CHOICE_MADE: 'notable',
  CHOICE_TIMED_OUT: 'notable',

  COMBAT_STARTED: 'trivial',
  COMBAT_WON: 'notable',
  COMBAT_FLED: 'notable',
  MONSTER_KILLED: 'notable',
  MONSTER_SPARED: 'pivotal',
  BOSS_KILLED: 'pivotal',
  BOSS_SPARED: 'pivotal',
  BOSS_RECRUITED: 'legendary',
  PLAYER_DIED: 'pivotal',

  NPC_MET: 'notable',
  NPC_SPOKE: 'trivial',
  NPC_HELPED: 'notable',
  NPC_SAVED: 'pivotal',
  NPC_KILLED: 'pivotal',
  NPC_BETRAYED: 'pivotal',
  NPC_ROBBED: 'notable',
  NPC_PERSUADED: 'notable',
  NPC_LIED_TO: 'notable',
  PROMISE_MADE: 'pivotal',
  PROMISE_KEPT: 'pivotal',
  PROMISE_BROKEN: 'pivotal',

  ITEM_GAINED: 'notable',
  ITEM_LOST: 'notable',
  ITEM_USED: 'trivial',
  CARD_DRAWN: 'trivial',
  CARD_PLAYED: 'trivial',
  CARD_TRANSFORMED: 'pivotal',
  CARD_REMOVED: 'notable',

  FACT_LEARNED: 'notable',
  RUMOR_HEARD: 'notable',

  EASTER_EGG_FOUND: 'legendary',
  TITLE_EARNED: 'pivotal',
  CHRONICLE_CHAPTER: 'pivotal',
  ANALYTICS: 'trivial',
};

export interface GameEvent {
  readonly seq: number;
  /** In-game tick. One tick == one resolved player action. */
  readonly t: number;
  readonly type: EventType;
  readonly significance: Significance;
  readonly actor?: EntityId;
  readonly target?: EntityId;
  readonly room?: RoomId;
  readonly data?: Readonly<Record<string, JsonValue>>;
  /**
   * The roll that produced this event. This one field is why the Chronicle can
   * say "the 6 you rolled in the flooded hall is why she trusts you", and why
   * Dice Memory needs no bookkeeping of its own.
   */
  readonly rollRef?: number;
}

export type EventDraft = Omit<GameEvent, 'seq' | 'significance'> & {
  significance?: Significance;
};

/**
 * Append-only log with significance filtering and bounded retention.
 *
 * `trivial` events are dispatched to reducers but never retained, so gameplay
 * can emit them freely for UI/audio purposes without growing the save.
 */
export class EventLog {
  private events: GameEvent[] = [];
  private nextSeq = 1;
  /** Number of trivial events dropped, purely for telemetry sanity checks. */
  private droppedTrivial = 0;
  /**
   * Number of `notable` events folded out by retention.
   *
   * 이게 0 이 아니면 **리플레이가 더 이상 상태를 완전히 복원하지 못한다** —
   * 접힌 이벤트에서 파생된 값(NPC 신뢰도 같은)은 스냅샷에만 남는다. 이 수를
   * 밖에서 볼 수 없던 동안, 결정성 테스트는 "런이 짧아서 아직 안 접혔다"는
   * 우연에 기대고 있었고, 런이 길어지자 곧바로 깨졌다.
   */
  private prunedNotable = 0;

  constructor(
    /** Retain at most this many `notable` events; pivotal+ are never dropped. */
    private readonly notableRetention = 400,
  ) {}

  get length(): number {
    return this.events.length;
  }

  get seqCursor(): number {
    return this.nextSeq;
  }

  get trivialDropped(): number {
    return this.droppedTrivial;
  }

  /** `notable` events folded out by retention. 0 이면 리플레이가 완전하다. */
  get notablePruned(): number {
    return this.prunedNotable;
  }

  /** Assigns a seq and retains the event if it clears the significance bar. */
  append(draft: EventDraft): GameEvent {
    const significance = draft.significance ?? DEFAULT_SIGNIFICANCE[draft.type];
    const event: GameEvent = { ...draft, seq: this.nextSeq++, significance };
    if (significance === 'trivial') {
      this.droppedTrivial++;
      return event;
    }
    this.events.push(event);
    this.prune();
    return event;
  }

  /**
   * Fold the oldest `notable` events out of the log once we exceed retention.
   * pivotal and legendary events are permanent: they are the ones the world is
   * allowed to remember decades later (descendants, revenge, server history).
   */
  private prune(): void {
    const notableCount = this.events.filter((e) => e.significance === 'notable').length;
    if (notableCount <= this.notableRetention) return;
    let toDrop = notableCount - this.notableRetention;
    this.prunedNotable += toDrop;
    this.events = this.events.filter((e) => {
      if (toDrop > 0 && e.significance === 'notable') {
        toDrop--;
        return false;
      }
      return true;
    });
  }

  all(): readonly GameEvent[] {
    return this.events;
  }

  /** Events at or above a significance floor, newest last. */
  atLeast(floor: Significance): GameEvent[] {
    return this.events.filter((e) => atLeast(e.significance, floor));
  }

  ofType(...types: EventType[]): GameEvent[] {
    const set = new Set(types);
    return this.events.filter((e) => set.has(e.type));
  }

  /** Did `type` ever happen, optionally against a specific target? */
  happened(type: EventType, target?: EntityId): boolean {
    return this.events.some((e) => e.type === type && (target === undefined || e.target === target));
  }

  countOf(type: EventType, target?: EntityId): number {
    return this.events.filter(
      (e) => e.type === type && (target === undefined || e.target === target),
    ).length;
  }

  involving(entity: EntityId): GameEvent[] {
    return this.events.filter((e) => e.actor === entity || e.target === entity);
  }

  /** Every event caused by one roll — the basis of "that roll did this". */
  causedByRoll(rollSeq: number): GameEvent[] {
    return this.events.filter((e) => e.rollRef === rollSeq);
  }

  /** Serialise for `events.jsonl`. */
  toJSONL(): string {
    return this.events.map((e) => JSON.stringify(e)).join('\n');
  }

  static fromJSONL(jsonl: string, notableRetention = 400): EventLog {
    const log = new EventLog(notableRetention);
    const lines = jsonl.split('\n').filter((l) => l.trim().length > 0);
    for (const line of lines) {
      const e = JSON.parse(line) as GameEvent;
      log.events.push(e);
      log.nextSeq = Math.max(log.nextSeq, e.seq + 1);
    }
    return log;
  }
}
